<template>
  <div class="relative flex justify-between items-center px-3 py-3 border-t">
    <div class="my-auto text-sm text-gray-brand-600 rounded-full tabular-nums">
      <transition name="strike" :duration="{ enter: 850, leave: 600 }">
        <span v-if="showStrikethroughPrice && showPrice" class="strike-wrap inline-block">
          <s>{{ strikethroughDisplayPrice }}</s>
          &nbsp;
        </span>
      </transition>
      <span v-if="showFreePrice" class="font-bold inline-block">Free</span>
      <span v-else-if="showPrice" class="font-bold inline-block" :class="activePriceFadeClass">
        {{ activeDisplayPrice }}
      </span>
    </div>
    <V2ProductQuantity v-if="openForOrders" :product="product" :submittable-cart-item="submittableCartItem" />
  </div>
</template>

<script>
import V2ProductQuantity from '@/components/v2/V2ProductQuantity'
import { formatPrice } from '@/composables/useCartMutation'
import { resolveMembershipPricing } from '@/utils/v2MembershipPricing'

export default {
  name: 'V2ProductPricing',

  components: { V2ProductQuantity },

  inject: {
    v2OpenForOrders: { default: null },
    v2CustomerMembershipTierId: { default: null },
    v2ActivePricingTierId: { default: null },
    v2DefaultMembershipTierId: { default: null }
  },

  props: {
    product: {
      type: Object,
      required: true
    },
    submittableCartItem: {
      type: Object,
      default: null
    },
    v2CartItem: {
      type: Object,
      default: null
    },
    pricingTiers: {
      type: Array,
      default: () => []
    },
    isUpsell: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return { strikeAnimDirection: null }
  },

  watch: {
    showStrikethroughPrice(newVal, oldVal) {
      if (newVal && !oldVal) this.strikeAnimDirection = 'enter'
      else if (!newVal && oldVal) this.strikeAnimDirection = 'leave'
      else return
      if (this._strikeAnimTimer) clearTimeout(this._strikeAnimTimer)
      this._strikeAnimTimer = setTimeout(() => {
        this.strikeAnimDirection = null
      }, 1500)
    }
  },

  beforeUnmount() {
    if (this._strikeAnimTimer) clearTimeout(this._strikeAnimTimer)
  },

  computed: {
    activePriceFadeClass() {
      if (this.strikeAnimDirection === 'enter') return 'fade-after-strike-draw'
      return ''
    },
    openForOrders() {
      if (this.v2OpenForOrders !== null) return this.v2OpenForOrders
      return this.$store.getters.currentBottle?.attributes?.status_for_placing_orders?.status === 'open'
    },
    displayPrice() {
      return this.product.price_formatted?.format
    },
    currency() {
      return this.product.price_formatted?.currency_iso || 'USD'
    },
    hasUpsellPrice() {
      return this.product.upsell_price_cents != null && this.product.upsell_price_cents < this.product.price_cents
    },
    upsellDisplayPrice() {
      return this.product.upsell_price_formatted?.format || formatPrice(this.product.upsell_price_cents, this.currency)
    },
    membershipPricing() {
      const tierId =
        this.v2CustomerMembershipTierId?.value !== undefined
          ? this.v2CustomerMembershipTierId.value
          : this.v2CustomerMembershipTierId
      return resolveMembershipPricing(this.product, tierId)
    },
    activePricingTierId() {
      return this.v2ActivePricingTierId?.value !== undefined
        ? this.v2ActivePricingTierId.value
        : this.v2ActivePricingTierId
    },
    customerMembershipTierId() {
      return this.v2CustomerMembershipTierId?.value !== undefined
        ? this.v2CustomerMembershipTierId.value
        : this.v2CustomerMembershipTierId
    },
    defaultMembershipTierId() {
      return this.v2DefaultMembershipTierId?.value !== undefined
        ? this.v2DefaultMembershipTierId.value
        : this.v2DefaultMembershipTierId
    },
    defaultPricingTierId() {
      return this.pricingTiers.find((t) => t.is_default)?.id ?? null
    },
    storePricingTierEntry() {
      return this.findPricingTierPricesEntry(this.activePricingTierId, this.customerMembershipTierId)
    },
    defaultStorePricingTierEntry() {
      const candidates = [
        this.findPricingTierPricesEntry(this.defaultPricingTierId, this.defaultMembershipTierId),
        this.findPricingTierPricesEntry(this.defaultPricingTierId, this.customerMembershipTierId)
      ].filter((e) => e && e.price_cents != null)
      if (!candidates.length) return null
      return candidates.reduce((a, b) => (a.price_cents >= b.price_cents ? a : b))
    },
    storePricingTierHasDiscount() {
      const active = this.storePricingTierEntry
      const base = this.defaultStorePricingTierEntry
      if (!active || !base) return false
      if (active.price_cents == null || base.price_cents == null) return false
      return active.price_cents < base.price_cents
    },
    cartItemDisplayPrice() {
      return this.v2CartItem?.display_price || null
    },
    cartItemHasDiscount() {
      if (!this.v2CartItem) return false
      if (typeof this.v2CartItem.strike_through === 'boolean') return this.v2CartItem.strike_through
      const dp = this.cartItemDisplayPrice
      if (!dp || dp.price == null || dp.strike_through_price == null) return false
      return dp.strike_through_price > dp.price
    },
    showStrikethroughPrice() {
      if (this.product.show_prices === false) return false
      if (this.hasUpsellPrice) return true
      if (this.storePricingTierHasDiscount) return true
      if (this.v2CartItem) return this.cartItemHasDiscount
      if (this.membershipPricing?.shouldStrikeThrough) return true
      return false
    },
    strikethroughDisplayPrice() {
      if (this.hasUpsellPrice) {
        return this.displayPrice
      }
      if (this.storePricingTierHasDiscount) {
        const base = this.defaultStorePricingTierEntry
        const variantsCents = this.v2CartItem?.variants_price || 0
        if (variantsCents > 0 && base?.price_cents != null) {
          return formatPrice(base.price_cents + variantsCents, this.currency)
        }
        return base?.price_formatted?.format || formatPrice(base?.price_cents, this.currency)
      }
      if (this.cartItemHasDiscount) {
        const dp = this.cartItemDisplayPrice
        return dp.strike_through_price_formatted?.format || formatPrice(dp.strike_through_price, this.currency)
      }
      return this.membershipPricing?.defaultPriceFormatted || this.displayPrice
    },
    activeDisplayPrice() {
      if (this.hasUpsellPrice) {
        return this.upsellDisplayPrice
      }
      if (this.cartItemDisplayPrice) {
        const dp = this.cartItemDisplayPrice
        return dp.price_formatted?.format || formatPrice(dp.price, this.currency)
      }
      if (this.v2CartItem) {
        return this.v2CartItem.total_price_formatted?.format || formatPrice(this.v2CartItem.total_price, this.currency)
      }
      if (this.storePricingTierEntry) {
        return (
          this.storePricingTierEntry.price_formatted?.format ||
          formatPrice(this.storePricingTierEntry.price_cents, this.currency)
        )
      }
      if (this.membershipPricing) {
        return this.membershipPricing.membershipPriceFormatted
      }
      return this.displayPrice
    },
    showFreePrice() {
      if (this.product.show_prices === false) return false
      if (this.membershipPricing) return false
      return this.product.price_cents === 0 && this.product.variants.length === 0
    },
    showPrice() {
      if (this.product.show_prices === false) return false
      return this.product.variants.length === 0 || this.product.price_cents > 0
    }
  },

  methods: {
    findPricingTierPricesEntry(pricingTierId, membershipTierId) {
      if (pricingTierId == null || membershipTierId == null) return null
      const entries = this.product?.pricing_tier_prices || []
      return (
        entries.find(
          (entry) =>
            String(entry.pricing_tier_id) === String(pricingTierId) &&
            String(entry.membership_tier_id) === String(membershipTierId)
        ) || null
      )
    }
  }
}
</script>

<style>
.strike-wrap s {
  text-decoration: none;
  position: relative;
  display: inline-block;
}
.strike-wrap s::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: currentColor;
  transform: scaleX(0);
  transform-origin: left center;
  animation: strike-draw 0.6s 0.25s ease-out forwards;
}
.strike-leave-active s::after {
  animation: strike-undraw 0.6s ease-in both;
}
@keyframes strike-draw {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@keyframes strike-undraw {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
.fade-after-strike-draw {
  animation: fade-after-strike-draw 1.35s ease-out forwards;
}
@keyframes fade-after-strike-draw {
  0%,
  63% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
